import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { KeycloakEventTypeLegacy, KeycloakService } from 'keycloak-angular';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment as env } from 'src/environments/environment';
import { useAuth0Provider } from '../auth-mode';

// Auth0 SDK errors that mean the session is gone and only a new sign-in gets a token.
const AUTH0_SESSION_ERRORS = ['login_required', 'consent_required', 'interaction_required', 'missing_refresh_token', 'invalid_grant'];

// A second expiry this soon after sending the user to sign in means the API rejects a fresh token (configuration, not
// expiry): the page shows the error instead of bouncing between the app and the sign-in page.
const REDIRECT_GUARD_KEY = 'pr1core.sessionExpiryRedirectAt';
const REDIRECT_GUARD_MS = 2 * 60 * 1000;

/**
 * Sends the user to sign in when their session has expired, instead of leaving a page whose every click fails.
 * Keycloak (dev, QA) refreshes quietly and, once the refresh token has expired too, sends the request with the stale
 * token, so the API answers 401; Auth0 (staging, production) fails to get a token at all (login_required). After
 * signing in the user comes back to the page they were on.
 *
 * Not used in Shopify mode: the embedded app gets a fresh session token from App Bridge on every request, and there
 * is no sign-in page of ours to send the user to.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private redirecting = false;

  constructor(
    private router: Router,
    @Optional() private keycloak: KeycloakService | null,
    @Optional() private auth0: AuthService | null
  ) {
    // Keycloak reports a failed background refresh before any click does.
    if (this.enabled && !useAuth0Provider()) {
      this.keycloak?.keycloakEvents$.subscribe(event => {
        if (event.type === KeycloakEventTypeLegacy.OnAuthRefreshError) {
          this.signInAgain();
        }
      });
    }
  }

  private get enabled(): boolean {
    return env.appMode !== 'shopify';
  }

  isSessionExpiry(request: HttpRequest<unknown>, error: unknown): boolean {
    if (!this.enabled) {
      return false;
    }
    if (error instanceof HttpErrorResponse) {
      return error.status === 401 && this.isPortalApi(request.url);
    }
    const code = (error as { error?: string } | null)?.error;
    return typeof code === 'string' && AUTH0_SESSION_ERRORS.includes(code);
  }

  signInAgain(): void {
    if (this.redirecting || this.redirectedRecently()) {
      return;
    }
    this.redirecting = true;
    try {
      sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
    } catch {
      // Storage blocked: the in-memory flag still prevents a second redirect from this page.
    }

    if (useAuth0Provider()) {
      this.auth0?.loginWithRedirect({ appState: { target: this.router.url } });
    } else {
      void this.keycloak?.login({ redirectUri: window.location.href });
    }
  }

  private redirectedRecently(): boolean {
    try {
      const at = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY));
      return at > 0 && Date.now() - at < REDIRECT_GUARD_MS;
    } catch {
      return false;
    }
  }

  // Only the portal's own APIs: a 401 from a third party (a signed storage URL, a CDN) is not our session.
  private isPortalApi(url: string): boolean {
    const bases = [env.pr1ApiUrl, (env as { commentApiUrl?: string }).commentApiUrl]
      .filter((base): base is string => !!base)
      .map(base => base.replace(/\/Private\/?$/i, ''));
    return bases.some(base => url.startsWith(base));
  }
}

@Injectable()
export class SessionExpiryInterceptor implements HttpInterceptor {
  constructor(private session: SessionExpiryService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (this.session.isSessionExpiry(request, error)) {
          this.session.signInAgain();
        }
        return throwError(() => error);
      })
    );
  }
}
